import React, { useEffect, useMemo, useRef, useState } from "react";
import type { PopupEvent, PopupRuntimeContext, PublishedPopup } from "./types";
import { PopupNode } from "./PopupNode";
import { canShowPopup, markClosed, markConverted, markShown } from "./popupStorage";
import { buildPopupTrackRequest } from "./popupTracking";
import { useRendererApi } from "../hooks/rendererApi/useRendererApi";

// Orquestrador client-side dos popups no site publicado: escolhe o popup elegível
// (página + dispositivo + frequência), aplica o gatilho (tempo/rolagem/saída), monta
// o container (dialog ou barra fixa) e dispara tracking de impressão/conversão/fechamento.

interface DynamicPopupRendererProps {
  popups: PublishedPopup[];
  ctx: PopupRuntimeContext;
  // Formulários salvos (bloco Formulário) resolvidos por id, buscados no S3 pelo renderer.
  forms?: Record<string, unknown>;
}

const normalizeSlug = (slug: string): string => {
  const s = (slug || "/").trim();
  return s === "" ? "/" : s;
};

const matchesPage = (popup: PublishedPopup, currentSlug: string): boolean => {
  const rules = popup.displayRules;
  if (!rules || rules.visibilityMode === "VISIBLE_ON_ALL" || rules.pageTargeting === "all") {
    return true;
  }
  // selectedPageSlugs é resolvido na publicação (id → slug).
  const slugs = (rules as unknown as { selectedPageSlugs?: string[] }).selectedPageSlugs ?? [];
  const current = normalizeSlug(currentSlug);
  const listed = slugs.map(normalizeSlug).includes(current);
  if (rules.visibilityMode === "ALL_EXCEPT" || rules.pageTargeting === "except") {
    return !listed;
  }
  return listed; // CUSTOM_PER_PAGE / only
};

const matchesDevice = (popup: PublishedPopup, isMobile: boolean): boolean => {
  const rules = popup.displayRules;
  if (!rules) return true;
  return isMobile ? rules.showOnMobile !== false : rules.showOnDesktop !== false;
};

export const DynamicPopupRenderer = ({ popups, ctx, forms = {} }: DynamicPopupRendererProps) => {
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  // Popups já fechados nesta page view não voltam a ser candidatos (evita reabrir em
  // loop quando o gatilho re-arma após fechar).
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Dedup de conversão por page-view (uma conversão por abertura do popup).
  const convertedRef = useRef(false);
  const rendererApi = useRendererApi();

  const track = (popupId: string, event: PopupEvent) => {
    const request = buildPopupTrackRequest({ ctx, popupId, event });
    if (request) rendererApi.trackPopupEvent(request);
  };

  useEffect(() => {
    setMounted(true);
    const sync = () => setIsMobile(window.innerWidth < 768);
    sync();
    // Responde a resize/rotação para a visibilidade por dispositivo (popup e blocos)
    // acompanhar a troca de breakpoint.
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);

  const candidate = useMemo(() => {
    if (!mounted) return null;
    return (
      popups.find(
        (p) =>
          p.structure &&
          !dismissed.has(p.id) &&
          matchesPage(p, ctx.requestPath ?? ctx.currentSlug) &&
          matchesDevice(p, isMobile) &&
          canShowPopup(p)
      ) ?? null
    );
  }, [popups, mounted, isMobile, ctx.requestPath, ctx.currentSlug, dismissed]);

  // Gatilhos do popup candidato.
  useEffect(() => {
    if (!candidate || openId) return;
    const { triggers } = candidate;
    const open = () => setOpenId(candidate.id);

    const cleanups: Array<() => void> = [];

    if (triggers?.timeOnPage?.enabled) {
      const ms = Math.max(0, (triggers.timeOnPage.seconds ?? 0) * 1000);
      const t = setTimeout(open, ms);
      cleanups.push(() => clearTimeout(t));
    }

    if (triggers?.scrollPercentage?.enabled) {
      const onScroll = () => {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        const pct = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0;
        if (pct >= (triggers.scrollPercentage.percentage ?? 50)) open();
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      cleanups.push(() => window.removeEventListener("scroll", onScroll));
    }

    if (triggers?.exitIntent?.enabled) {
      const onMouseOut = (e: MouseEvent) => {
        if (e.clientY <= 0) open();
      };
      document.addEventListener("mouseout", onMouseOut);
      cleanups.push(() => document.removeEventListener("mouseout", onMouseOut));
    }

    // Sem nenhum gatilho ativo, não abre (evita popup sempre-ligado sem regra).
    return () => cleanups.forEach((fn) => fn());
  }, [candidate, openId]);

  // Impressão ao abrir.
  useEffect(() => {
    if (!openId || !candidate) return;
    markShown(candidate.id);
    track(candidate.id, "impression");
  }, [openId]);

  if (!mounted || !candidate || openId !== candidate.id || !candidate.structure) {
    return null;
  }

  const popup = candidate;
  const structure = popup.structure;
  if (!structure) return null;
  const rootId = structure.ROOT ? "ROOT" : Object.keys(structure)[0];

  const handleClose = () => {
    markClosed(popup.id);
    track(popup.id, "close");
    setDismissed((prev) => new Set(prev).add(popup.id));
    setOpenId(null);
  };

  // Conta a conversão no máximo uma vez por page-view (dedup) e persiste para a
  // frequência (hideAfterConversion).
  const recordConversion = () => {
    if (convertedRef.current) return;
    convertedRef.current = true;
    markConverted(popup.id);
    track(popup.id, "conversion");
  };

  // O formulário sempre captura o lead (submit interno do PopupForm); mas só conta como
  // conversão quando a meta do popup é "envio de formulário".
  const handleFormConverted = () => {
    if (popup.conversionGoal?.type === "form_submit") recordConversion();
  };

  // Clique no botão-alvo de conversão (só quando a meta é "clique em botão").
  const handleContainerClick = (e: React.MouseEvent) => {
    if (popup.conversionGoal?.type !== "button_click" || !popup.conversionGoal.buttonId) return;
    const target = e.target as HTMLElement;
    if (target.closest(`#${CSS.escape(popup.conversionGoal.buttonId)}`)) {
      recordConversion();
    }
  };

  const content = (
    <div ref={containerRef} onClick={handleContainerClick}>
      <PopupNode
        nodeId={rootId}
        tree={structure}
        ctx={ctx}
        popupId={popup.id}
        popupName={popup.name}
        onConverted={handleFormConverted}
        onClose={handleClose}
        forms={forms}
        isMobile={isMobile}
      />
    </div>
  );

  if (popup.type === "fixed_bar") {
    const cfg = (popup.fixedBarConfig ?? {}) as Record<string, any>;
    const isBottom = cfg.position === "bottom";
    const closeSize = (cfg.closeButton?.size as number) || 14;
    return (
      <div
        role="dialog"
        aria-label={popup.name}
        style={{
          position: "fixed",
          ...(isBottom ? { bottom: 0 } : { top: 0 }),
          left: 0,
          right: 0,
          zIndex: 9998,
          backgroundColor: cfg.backgroundColor || "#FFFFFF",
          ...(cfg.backgroundImage
            ? { backgroundImage: `url(${cfg.backgroundImage})`, backgroundSize: "cover", backgroundPosition: "center" }
            : {}),
          padding: `${cfg.padding ?? 16}px`,
          // Sombra só para fora da barra: para cima no rodapé, para baixo no topo.
          boxShadow: cfg.shadow
            ? (isBottom ? "0 -2px 8px rgba(0,0,0,0.15)" : "0 2px 8px rgba(0,0,0,0.15)")
            : "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ flex: 1 }}>{content}</div>
        {cfg.closeButton?.enabled !== false && (
          <button
            type="button"
            aria-label="Fechar"
            onClick={handleClose}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              width: closeSize + 12,
              height: closeSize + 12,
              borderRadius: "50%",
              background: cfg.closeButton?.backgroundColor || "transparent",
              border: "none",
              color: cfg.closeButton?.color || "#FFFFFF",
              fontSize: closeSize + 4,
              lineHeight: 1,
              cursor: "pointer",
              marginLeft: 12,
            }}
          >
            ×
          </button>
        )}
      </div>
    );
  }

  // dialog (modal centralizado com backdrop)
  const cfg = (popup.dialogConfig ?? {}) as Record<string, any>;
  const backdrop = cfg.backdrop ?? {};
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={popup.name}
      onClick={backdrop.closeOnClick ? handleClose : undefined}
      style={{
        // O container é transparente; a cor + opacidade ficam SÓ na camada de backdrop
        // abaixo. Antes o container tinha fundo preto opaco (opacity:1) e a página
        // publicada saía 100% preta, ignorando a opacidade configurada.
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      {/* camada de opacidade só no backdrop */}
      <div style={{ position: "absolute", inset: 0, backgroundColor: backdrop.color || "#000000", opacity: backdrop.opacity ?? 0.5 }} />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          zIndex: 1,
          width: cfg.width ?? 520,
          maxWidth: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          backgroundColor: cfg.backgroundColor || "#FFFFFF",
          ...(cfg.backgroundImage
            ? { backgroundImage: `url(${cfg.backgroundImage})`, backgroundSize: "cover", backgroundPosition: "center" }
            : {}),
          borderRadius: `${cfg.borderRadius ?? 12}px`,
          padding: `${cfg.padding ?? 24}px`,
        }}
      >
        {cfg.closeButton?.enabled !== false && (() => {
          const closeSize = (cfg.closeButton?.size as number) || 14;
          return (
            <button
              type="button"
              aria-label="Fechar"
              onClick={handleClose}
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: cfg.closeButton?.backgroundColor || "#E5E7EB",
                border: "none",
                color: cfg.closeButton?.color || "#6B7280",
                width: closeSize + 12,
                height: closeSize + 12,
                borderRadius: "50%",
                cursor: "pointer",
                fontSize: closeSize + 4,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          );
        })()}
        {content}
      </div>
    </div>
  );
};
